import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  Input,
  ModalDialog,
  Numeric,
  TableCell,
  TableHeadCell,
  TableRow,
  Textarea,
  TranslatableField,
} from "@rothoblaas/design-system";
import { translatableFieldProps } from "../../../../utils/translatableField";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminServiceClass } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

interface ServiceClassFormBody {
  classNumber: number | null;
  name: string;
  nameIt: string;
  nameDe: string;
  nameFr: string;
  nameEs: string;
  description: string;
  descriptionIt: string;
  descriptionDe: string;
  descriptionFr: string;
  descriptionEs: string;
  typicalExamples: string;
  order: number | null;
}

const AdminServiceClassesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "serviceClasses" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useServiceClasses(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminServiceClass>();
  const [itemToEdit, setItemToEdit] = useState<AdminServiceClass>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminServiceClass>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.serviceClasses.classNumber}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.classNumber}</TableCell>
              <TableCell>{item.name}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.serviceClasses.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteServiceClass(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <ServiceClassFormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <ServiceClassFormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const ServiceClassFormModal: FC<{ item?: AdminServiceClass; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();

  const defaultValues: ServiceClassFormBody = {
    classNumber: item?.classNumber ?? null,
    name: item?.name ?? "",
    nameIt: item?.nameIt ?? "",
    nameDe: item?.nameDe ?? "",
    nameFr: item?.nameFr ?? "",
    nameEs: item?.nameEs ?? "",
    description: item?.description ?? "",
    descriptionIt: item?.descriptionIt ?? "",
    descriptionDe: item?.descriptionDe ?? "",
    descriptionFr: item?.descriptionFr ?? "",
    descriptionEs: item?.descriptionEs ?? "",
    typicalExamples: item?.typicalExamples ?? "",
    order: item?.order ?? null,
  };

  const onSubmit = () => async (formData: ServiceClassFormBody) => {
    if (formData.classNumber == null) return;
    const body = {
      classNumber: formData.classNumber,
      name: formData.name,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameFr: formData.nameFr || null,
      nameEs: formData.nameEs || null,
      description: formData.description || null,
      descriptionIt: formData.descriptionIt || null,
      descriptionDe: formData.descriptionDe || null,
      descriptionFr: formData.descriptionFr || null,
      descriptionEs: formData.descriptionEs || null,
      typicalExamples: formData.typicalExamples || null,
      order: formData.order || null,
    };
    try {
      if (item) {
        await adminApi.updateServiceClass(item.id, body);
      } else {
        await adminApi.insertServiceClass(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.serviceClasses.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.serviceClasses.singular}`
      }
    >
      <Form<ServiceClassFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control, setValue }) => {
          const formValues = useWatch({ control });
          return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="classNumber"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.serviceClasses.classNumber}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <TranslatableField
              {...translatableFieldProps("name", formValues, setValue as any, i18n.translation)}
              dialogTitle={i18n.translation.common.name}
            >
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.name} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                    )}
                  </FormField>
                )}
              />
            </TranslatableField>
            <TranslatableField
              {...translatableFieldProps("description", formValues, setValue as any, i18n.translation)}
              dialogTitle={i18n.translation.lockCalculatorModule.admin.serviceClasses.description}
              type="textarea"
            >
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.translation.lockCalculatorModule.admin.serviceClasses.description}
                    error={fieldState.error}
                   
                  >
                    {({ labelId, isInvalid }) => (
                      <Textarea id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                    )}
                  </FormField>
                )}
              />
            </TranslatableField>
            <Controller
              name="typicalExamples"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.serviceClasses.typicalExamples}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.serviceClasses.order}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );}}
      </Form>
    </ModalDialog>
  );
};

export default AdminServiceClassesTab;
